import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, formatCOP, imageUrl } from '../services/api';
import { displayNombre, needsNombreOcr } from '../utils/productName';
import CachedProductImage from './CachedProductImage';
import { warmProductImage } from '../utils/imageCache';
import {
  allOpcionesAgotadas,
  firstDisponibleOpcion,
  needsOpcionPick,
  realOpciones,
} from '../utils/productoOpciones';

function isSinStock(product) {
  if (!product) return true;
  if (product.disponibilidad === 'sin_stock') return true;
  if (product.en_stock != null && Number(product.en_stock) === 0) return true;
  if (product.stock != null && Number(product.stock) === 0) return true;
  return product.cantidad != null && Number(product.cantidad) <= 0;
}

/** Lightbox tipo galería: pellizca, arrastra para mover, doble toque, rueda. */
function ImageZoomLightbox({ src, alt, onClose }) {
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const stateRef = useRef({ scale: 1, x: 0, y: 0 });
  const gestureRef = useRef(null);
  const [, bump] = useState(0);

  function applyTransform(animate) {
    const img = imgRef.current;
    if (!img) return;
    const { scale, x, y } = stateRef.current;
    img.style.transition = animate ? 'transform 0.2s ease' : 'none';
    img.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
    img.style.cursor = scale > 1.02 ? 'grab' : 'zoom-in';
  }

  function setTransform({ scale, x, y }, animate = false) {
    const nextScale = Math.min(5, Math.max(1, scale));
    let nx = x;
    let ny = y;
    if (nextScale <= 1.02) {
      nx = 0;
      ny = 0;
    } else {
      // Limitar el arrastre para que no se vaya demasiado lejos
      const max = (nextScale - 1) * 280;
      nx = Math.max(-max, Math.min(max, nx));
      ny = Math.max(-max, Math.min(max, ny));
    }
    stateRef.current = { scale: nextScale, x: nx, y: ny };
    applyTransform(animate);
    bump((n) => n + 1);
  }

  useEffect(() => {
    applyTransform(false);
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function midTouch(touches) {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    };
  }

  function onTouchStart(e) {
    if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      gestureRef.current = {
        type: 'pinch',
        dist,
        scale: stateRef.current.scale,
        x: stateRef.current.x,
        y: stateRef.current.y,
        mid: midTouch(e.touches),
      };
      return;
    }
    if (e.touches.length === 1) {
      const t = e.touches[0];
      gestureRef.current = {
        type: 'pan',
        x0: t.clientX,
        y0: t.clientY,
        ox: stateRef.current.x,
        oy: stateRef.current.y,
        scale: stateRef.current.scale,
        moved: false,
      };
    }
  }

  function onTouchMove(e) {
    const g = gestureRef.current;
    if (!g) return;
    if (e.touches.length === 2 && g.type === 'pinch') {
      if (e.cancelable) e.preventDefault();
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const ratio = dist / Math.max(1, g.dist);
      const nextScale = g.scale * ratio;
      const mid = midTouch(e.touches);
      // Pan ligero siguiendo el centro del pellizco
      setTransform({
        scale: nextScale,
        x: g.x + (mid.x - g.mid.x),
        y: g.y + (mid.y - g.mid.y),
      });
      return;
    }
    if (e.touches.length === 1 && g.type === 'pan' && g.scale > 1.02) {
      if (e.cancelable) e.preventDefault();
      const t = e.touches[0];
      const dx = t.clientX - g.x0;
      const dy = t.clientY - g.y0;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) g.moved = true;
      setTransform({ scale: g.scale, x: g.ox + dx, y: g.oy + dy });
    }
  }

  function onTouchEnd(e) {
    const g = gestureRef.current;
    gestureRef.current = null;
    if (!g) return;
    // Tap simple con zoom 1 → ampliar un poco; con zoom → si no movió, dejar
    if (g.type === 'pan' && !g.moved && e.changedTouches?.length === 1) {
      // no-op here; double-tap handled separately
    }
    if (stateRef.current.scale <= 1.02) {
      setTransform({ scale: 1, x: 0, y: 0 }, true);
    }
  }

  function onDoubleClick(e) {
    e.stopPropagation();
    e.preventDefault();
    const { scale } = stateRef.current;
    if (scale > 1.2) {
      setTransform({ scale: 1, x: 0, y: 0 }, true);
    } else {
      setTransform({ scale: 2.5, x: 0, y: 0 }, true);
    }
  }

  function onWheel(e) {
    e.preventDefault();
    const { scale, x, y } = stateRef.current;
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setTransform({ scale: scale + delta, x, y });
  }

  function onPointerDown(e) {
    // Mouse / pen pan cuando hay zoom
    if (e.pointerType === 'touch') return;
    if (stateRef.current.scale <= 1.02) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    gestureRef.current = {
      type: 'mouse-pan',
      x0: e.clientX,
      y0: e.clientY,
      ox: stateRef.current.x,
      oy: stateRef.current.y,
      scale: stateRef.current.scale,
    };
  }

  function onPointerMove(e) {
    const g = gestureRef.current;
    if (!g || g.type !== 'mouse-pan') return;
    setTransform({
      scale: g.scale,
      x: g.ox + (e.clientX - g.x0),
      y: g.oy + (e.clientY - g.y0),
    });
  }

  function onPointerUp() {
    if (gestureRef.current?.type === 'mouse-pan') gestureRef.current = null;
  }

  const zoomed = stateRef.current.scale > 1.02;

  return createPortal(
    <div className="img-zoom-overlay" role="dialog" aria-modal="true">
      <button type="button" className="img-zoom-close" onClick={onClose} aria-label="Cerrar zoom">
        ✕
      </button>
      <p className="img-zoom-hint">
        {zoomed
          ? 'Arrastra para mover · pellizca para zoom · doble toque para reset'
          : 'Pellizca o doble toque para ampliar · arrastra cuando esté ampliada'}
      </p>
      <div
        ref={stageRef}
        className={`img-zoom-stage${zoomed ? ' is-zoomed' : ''}`}
        onWheel={onWheel}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onDoubleClick={onDoubleClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt || ''}
          className="img-zoom-img"
          draggable={false}
        />
      </div>
      <button type="button" className="img-zoom-backdrop-close" onClick={onClose} aria-label="Cerrar">
        Cerrar
      </button>
    </div>,
    document.body
  );
}

export default function ProductPreview({ product, products = [], onClose, onAdd, onNavigate }) {
  const [observaciones, setObservaciones] = useState('');
  const [cantidad, setCantidad] = useState(1);
  const [info, setInfo] = useState(product);
  const [loadingName, setLoadingName] = useState(false);
  const [compact, setCompact] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [opcionId, setOpcionId] = useState(null);
  const touchRef = useRef({ x: 0, y: 0, active: false, locked: null });
  const addedTimer = useRef(null);
  const obsByCodigo = useRef(new Map());
  const qtyByCodigo = useRef(new Map());

  const list = products.length ? products : product ? [product] : [];
  const currentIndex = list.findIndex((p) => p.codigo === product?.codigo);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < list.length - 1;

  useEffect(() => {
    if (!product?.codigo) return;
    setInfo(product);
    setObservaciones(obsByCodigo.current.get(product.codigo) || '');
    setCantidad(qtyByCodigo.current.get(product.codigo) || 1);
    setCompact(false);
    setJustAdded(false);
    setDragX(0);
    setDragging(false);
    setZoomOpen(false);
    const first = firstDisponibleOpcion(product);
    setOpcionId(first?.id || realOpciones(product)[0]?.id || null);
  }, [product]);

  useEffect(() => () => window.clearTimeout(addedTimer.current), []);

  useEffect(() => {
    if (!product || !needsNombreOcr(product.nombre)) return undefined;
    let cancelled = false;
    setLoadingName(true);
    api
      .extraerNombre(product.codigo)
      .then((res) => {
        if (!cancelled && res.data) setInfo((prev) => ({ ...prev, ...res.data }));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingName(false);
      });
    return () => {
      cancelled = true;
    };
  }, [product]);

  useEffect(() => {
    if (compact) return undefined;
    const main = document.querySelector('.app-main');
    const scrollY = main?.scrollTop ?? 0;
    const prevOverflow = main?.style.overflow ?? '';
    if (main) main.style.overflow = 'hidden';

    return () => {
      if (main) {
        main.style.overflow = prevOverflow;
        main.scrollTop = scrollY;
      }
    };
  }, [compact]);

  useEffect(() => {
    function onKey(e) {
      if (zoomOpen) return;
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        onNavigate?.(list[currentIndex - 1]);
      }
      if (e.key === 'ArrowRight' && currentIndex >= 0 && currentIndex < list.length - 1) {
        onNavigate?.(list[currentIndex + 1]);
      }
      if (e.key === 'Escape') {
        if (compact) setCompact(false);
        else onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentIndex, list, onNavigate, onClose, compact, zoomOpen]);

  const ops = realOpciones(info);
  const pickOpcion = needsOpcionPick(info);
  const selectedOp = ops.find((o) => o.id === opcionId) || firstDisponibleOpcion(info);
  const img = imageUrl(selectedOp?.imagen || info?.imagen);
  const nombre = displayNombre(info || {});
  const marca = info?.marca || '¡Oh! BELLEZA';
  const opcionAgotada = Boolean(selectedOp && !selectedOp.disponible);
  const sinStock = isSinStock(info) || allOpcionesAgotadas(info) || (pickOpcion && opcionAgotada);

  useEffect(() => {
    if (img) warmProductImage(img);
  }, [img]);

  if (!product) return null;

  function persistDraft(nextObs, nextQty) {
    if (!product?.codigo) return;
    obsByCodigo.current.set(product.codigo, nextObs);
    qtyByCodigo.current.set(product.codigo, nextQty);
  }

  function goPrev() {
    if (hasPrev) onNavigate?.(list[currentIndex - 1]);
  }

  function goNext() {
    if (hasNext) onNavigate?.(list[currentIndex + 1]);
  }

  function onTouchStart(e) {
    if (list.length < 2) return;
    const t = e.touches?.[0];
    if (!t) return;
    touchRef.current = { x: t.clientX, y: t.clientY, active: true, locked: null, dx: 0 };
    setDragging(true);
  }

  function onTouchMove(e) {
    const st = touchRef.current;
    if (!st.active) return;
    const t = e.touches?.[0];
    if (!t) return;
    const dx = t.clientX - st.x;
    const dy = t.clientY - st.y;
    if (st.locked == null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      st.locked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
    }
    if (st.locked !== 'h') return;
    if (e.cancelable) e.preventDefault();
    const max = 120;
    let next = dx;
    if (!hasPrev && next > 0) next *= 0.25;
    if (!hasNext && next < 0) next *= 0.25;
    next = Math.max(-max, Math.min(max, next));
    st.dx = next;
    touchRef.current = st;
    setDragX(next);
  }

  function onTouchEnd() {
    const st = touchRef.current;
    const dx = st.dx;
    touchRef.current = { x: 0, y: 0, active: false, locked: null, dx: 0 };
    setDragging(false);
    setDragX(0);
    if (st.locked !== 'h') return;
    if (dx <= -56 && hasNext) goNext();
    else if (dx >= 56 && hasPrev) goPrev();
  }

  function handleAdd() {
    if (sinStock) return;
    if (pickOpcion && selectedOp && !selectedOp.disponible) return;
    if (pickOpcion && !selectedOp) return;
    const qty = Math.max(1, parseInt(String(cantidad), 10) || 1);
    const extra = observaciones.trim();
    const label = selectedOp?.nombre || '';
    const obs = label ? (extra && extra !== label ? `${label} · ${extra}` : label) : extra;
    onAdd(
      { ...info, nombre: nombre || info.nombre, imagen: selectedOp?.imagen || info.imagen },
      { observaciones: obs, cantidad: qty, opcion_id: selectedOp?.id || null }
    );
    setJustAdded(true);
    window.clearTimeout(addedTimer.current);
    addedTimer.current = window.setTimeout(() => setJustAdded(false), 1800);
  }

  if (compact) {
    return createPortal(
      <div className="preview-compact-bar">
        <button type="button" className="preview-compact-nav" disabled={!hasPrev} onClick={goPrev} aria-label="Anterior">
          ‹
        </button>
        <button type="button" className="preview-compact-main" onClick={() => setCompact(false)}>
          {img ? (
            <CachedProductImage src={img} alt="" className="preview-compact-thumb" loading="eager" />
          ) : (
            <span className="preview-compact-thumb empty" />
          )}
          <span className="preview-compact-info">
            <strong>{info.codigo}</strong>
            <em>{formatCOP(info.precio)}</em>
          </span>
        </button>
        <button type="button" className="preview-compact-nav" disabled={!hasNext} onClick={goNext} aria-label="Siguiente">
          ›
        </button>
        <button type="button" className="preview-compact-add" onClick={handleAdd} disabled={sinStock}>
          {sinStock ? 'Agotado' : '+'}
        </button>
        <button type="button" className="preview-compact-close" onClick={onClose} aria-label="Cerrar">
          ✕
        </button>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="modal-overlay preview-overlay" onClick={onClose}>
      <div className="modal product-preview-v2" onClick={(e) => e.stopPropagation()}>
        <div className="preview-toolbar">
          {list.length > 1 ? (
            <span className="preview-nav-counter">
              {currentIndex + 1} / {list.length}
            </span>
          ) : (
            <span />
          )}
          <div className="preview-toolbar-actions">
            <button
              type="button"
              className="preview-tool-btn"
              onClick={() => setCompact(true)}
              title="Minimizar — seguir viendo catálogo"
            >
              ─
            </button>
            <button type="button" className="preview-tool-btn" onClick={onClose} aria-label="Cerrar">
              ✕
            </button>
          </div>
        </div>

        <div className="preview-scroll">
          <div
            className={`preview-hero${list.length > 1 ? ' preview-hero--swipe' : ''}${dragging ? ' is-dragging' : ''}`}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchEnd}
          >
            {list.length > 1 ? (
              <>
                <button
                  type="button"
                  className="preview-nav preview-nav-prev"
                  disabled={!hasPrev}
                  onClick={goPrev}
                  aria-label="Producto anterior"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="preview-nav preview-nav-next"
                  disabled={!hasNext}
                  onClick={goNext}
                  aria-label="Producto siguiente"
                >
                  ›
                </button>
              </>
            ) : null}
            {img ? (
              <button
                type="button"
                className="preview-hero-zoom-btn"
                onClick={() => setZoomOpen(true)}
                aria-label="Ampliar foto"
                title="Toca para ampliar"
              >
                <CachedProductImage
                  src={img}
                  alt={nombre || `Producto ${info.codigo}`}
                  className="preview-hero-img"
                  loading="eager"
                  fetchPriority="high"
                  draggable={false}
                  style={{
                    transform: dragX ? `translateX(${dragX}px)` : undefined,
                    transition: dragging ? 'none' : 'transform .2s ease',
                  }}
                />
                <span className="preview-zoom-badge">🔍 Ampliar</span>
              </button>
            ) : (
              <div className="preview-hero-empty">Sin imagen</div>
            )}
            {list.length > 1 ? (
              <p className="preview-swipe-hint" aria-hidden="true">
                Desliza ← → · toca la foto para zoom
              </p>
            ) : img ? (
              <p className="preview-swipe-hint" aria-hidden="true">
                Toca la foto para ampliar
              </p>
            ) : null}
          </div>

          <div className="preview-content">
            <div className="preview-meta-row">
              <span className="preview-badge">Cód. {info.codigo}</span>
              {info.subcategoria ? <span className="preview-badge soft">{info.subcategoria}</span> : null}
            </div>

            {loadingName ? (
              <p className="preview-loading">Leyendo nombre desde la foto...</p>
            ) : (
              <h2 className="preview-title">{nombre || `Producto ${info.codigo}`}</h2>
            )}

            <div className="preview-info-grid">
              {info.referencia ? (
                <div className="preview-info-item">
                  <span>Referencia</span>
                  <strong>{info.referencia}</strong>
                </div>
              ) : null}
              <div className="preview-info-item">
                <span>Marca</span>
                <strong>{marca}</strong>
              </div>
              {info.descripcion ? (
                <div className="preview-info-item full">
                  <span>Presentación</span>
                  <strong>{info.descripcion}</strong>
                </div>
              ) : null}
            </div>

            <div className="preview-price-box">
              <span>Precio</span>
              <strong>{formatCOP(info.precio)}</strong>
            </div>

            <div className="preview-form-section">
              {pickOpcion ? (
                <div className="preview-opciones">
                  <span>Opciones / sabores</span>
                  <div className="preview-opciones-grid">
                    {ops.map((op) => {
                      const src = imageUrl(op.imagen);
                      const on = selectedOp?.id === op.id;
                      return (
                        <button
                          key={op.id}
                          type="button"
                          className={`preview-opcion${on ? ' is-on' : ''}${op.disponible ? '' : ' is-agotada'}`}
                          onClick={() => setOpcionId(op.id)}
                        >
                          {src ? <CachedProductImage src={src} alt="" loading="lazy" /> : <span className="preview-opcion-ph" />}
                          <em>{op.nombre || `Opción`}</em>
                          {!op.disponible ? <strong>Agotada</strong> : null}
                        </button>
                      );
                    })}
                  </div>
                  {pickOpcion && !selectedOp?.disponible ? (
                    <p className="preview-opcion-warn">Esa opción está agotada. Elige otra.</p>
                  ) : null}
                </div>
              ) : null}
              <label className="preview-field">
                <span>Cantidad (puedes escribir el número)</span>
                <div className="qty-stepper">
                  <button
                    type="button"
                    disabled={sinStock}
                    onClick={() => {
                      setCantidad((n) => {
                        const cur = Math.max(1, parseInt(String(n), 10) || 1);
                        const next = Math.max(1, cur - 1);
                        persistDraft(observaciones, next);
                        return next;
                      });
                    }}
                  >
                    −
                  </button>
                  <input
                    className="qty-stepper-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    disabled={sinStock}
                    aria-label="Cantidad"
                    value={cantidad}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, '');
                      if (raw === '') {
                        setCantidad('');
                        return;
                      }
                      const next = Math.max(1, parseInt(raw, 10) || 1);
                      setCantidad(next);
                      persistDraft(observaciones, next);
                    }}
                    onBlur={() => {
                      const next = Math.max(1, parseInt(String(cantidad), 10) || 1);
                      setCantidad(next);
                      persistDraft(observaciones, next);
                    }}
                  />
                  <button
                    type="button"
                    disabled={sinStock}
                    onClick={() => {
                      setCantidad((n) => {
                        const cur = Math.max(1, parseInt(String(n), 10) || 1);
                        const next = cur + 1;
                        persistDraft(observaciones, next);
                        return next;
                      });
                    }}
                  >
                    +
                  </button>
                </div>
              </label>

              <label className="preview-field preview-field-obs">
                <span>Observaciones (las ve bodega)</span>
                <textarea
                  rows={3}
                  dir="ltr"
                  inputMode="text"
                  enterKeyHint="done"
                  placeholder="Color, talla, presentación, detalle para bodega…"
                  value={observaciones}
                  onChange={(e) => {
                    const v = e.target.value;
                    setObservaciones(v);
                    persistDraft(v, Math.max(1, parseInt(String(cantidad), 10) || 1));
                  }}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="preview-footer">
          <button
            type="button"
            className={`btn-primary btn-lg preview-cta${justAdded ? ' preview-cta--added' : ''}`}
            onClick={handleAdd}
            disabled={sinStock}
          >
            {sinStock
              ? pickOpcion && opcionAgotada && !allOpcionesAgotadas(info)
                ? 'Esa opción está agotada'
                : 'Producto agotado'
              : justAdded
                ? '✓ Agregado'
                : `Agregar al pedido · ${formatCOP(info.precio * cantidad)}`}
          </button>
        </div>
      </div>
      {zoomOpen && img ? (
        <ImageZoomLightbox src={img} alt={nombre || info.codigo} onClose={() => setZoomOpen(false)} />
      ) : null}
    </div>,
    document.body
  );
}
